import { useEffect, useRef, useState } from "react";

/**
 * SafeImg — <img> con fallback automatico se l'URL sorgente non carica.
 *
 * Uso:
 *   <SafeImg src={cmsUrl} fallback={DEFAULT_URL} alt="..." className="..." />
 *
 * Se `src` è vuoto → mostra direttamente `fallback`.
 * Se `src` fallisce a caricare → swappa a `fallback`.
 * Se anche `fallback` fallisce → mostra un placeholder neutro con l'alt text
 * (nessuna icona rotta del browser).
 *
 * IMPORTANTE: reagisce ai cambi di `src` provenienti da parent (es. quando
 * `useLandingConfig` completa il fetch async e passa il nuovo URL): un
 * `useEffect` sul prop `src` resetta lo stato interno.
 */
const PLACEHOLDER_DATA_URI =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 600'><rect width='800' height='600' fill='%23f4ede1'/><text x='400' y='300' font-family='serif' font-size='28' fill='%23a89a86' text-anchor='middle' alignment-baseline='middle'>Immagine in aggiornamento</text></svg>";

function resolveInitial(src, fallback) {
  const trimmed = (src && String(src).trim()) || "";
  if (trimmed) return trimmed;
  const fb = (fallback && String(fallback).trim()) || "";
  return fb || PLACEHOLDER_DATA_URI;
}

export default function SafeImg({ src, fallback, alt = "", className = "", loading = "lazy", ...rest }) {
  const [current, setCurrent] = useState(() => resolveInitial(src, fallback));
  // `stage` = quale livello del fallback stiamo mostrando:
  //   "src"         → URL principale dal parent
  //   "fallback"    → URL di default hardcoded del componente chiamante
  //   "placeholder" → SVG neutro
  const [stage, setStage] = useState(() => {
    if ((src && String(src).trim())) return "src";
    if ((fallback && String(fallback).trim())) return "fallback";
    return "placeholder";
  });
  const lastSrcRef = useRef(src);
  const lastFallbackRef = useRef(fallback);

  // Sync quando cambiano i props (parent aggiorna async, es. dopo fetch CMS)
  useEffect(() => {
    if (src === lastSrcRef.current && fallback === lastFallbackRef.current) return;
    lastSrcRef.current = src;
    lastFallbackRef.current = fallback;
    setCurrent(resolveInitial(src, fallback));
    if (src && String(src).trim()) setStage("src");
    else if (fallback && String(fallback).trim()) setStage("fallback");
    else setStage("placeholder");
  }, [src, fallback]);

  const handleError = () => {
    if (stage === "src" && fallback && current !== fallback) {
      setCurrent(fallback);
      setStage("fallback");
      return;
    }
    if (stage !== "placeholder" && current !== PLACEHOLDER_DATA_URI) {
      setCurrent(PLACEHOLDER_DATA_URI);
      setStage("placeholder");
    }
  };

  return (
    <img
      src={current}
      alt={alt}
      loading={loading}
      onError={handleError}
      className={className}
      {...rest}
    />
  );
}
